import React, { useEffect, useMemo, useState } from 'react';

import { Flex, Grid, GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useGetTimezoneOffsetEmployee } from 'components/hooks/useGetTimezoneOffsetEmployee';
import { format, isToday } from 'date-fns';
import { useDispatch } from 'react-redux';
import { RRule } from 'rrule';

import { DatePicker } from 'uiToolkitV1/DatePicker';
import { Paper } from 'uiToolkitV1/Paper';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextLabel } from 'uiToolkitV1/Typography';

import { CustomRecurrenceModal, getWhatDayOfTheMonth } from '../InstancePage/common/modals/CustomRecurrenceModal';
import { TimeCustomInput } from '../InstancePage/features/MeetingDetails/MeetingsDetailsExpanded';
import { dayCapitalizedRRuleString } from '../InstancePage/utils';
import {
  updateDateTime,
  updateDuration,
  updateFrequency,
  useCreateOneOnOneDrawerData,
} from './slice/CreateOneOnOneDrawerSlice';

interface Props {
  onSave?: () => void;
  onCancel?: () => void;
}

const Container = styled(Paper)`
  background: #ffffff;
  width: 100%;
  padding: 24px 20px;
`;

const DURATION_OPTIONS: { label: string; value: number }[] = [
  {
    label: '30 mins',
    value: 30,
  },
  {
    label: '45 mins',
    value: 45,
  },
  {
    label: '60 mins',
    value: 60,
  },
];

const numberToWords = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Fifth',
};

const MeetingsDetails = () => {
  const { startDateTime: selectedDate, duration, frequency: selectedFrequency } = useCreateOneOnOneDrawerData();
  const employeeTimezone = useGetTimezoneOffsetEmployee();

  const selectedDuration = DURATION_OPTIONS.find((option) => option.value === parseInt(duration, 10));

  const dispatch = useDispatch();
  const meetingFrequencies = useMemo(() => {
    const selectedDay = selectedDate ? format(selectedDate, 'EEEE') : null;
    const { nthDayOfTheMonth, isLastNthDayOfTheMonth } = getWhatDayOfTheMonth(selectedDate);
    const frequencies = [
      {
        label: `Weekly on ${selectedDay}`,
        value: new RRule({
          freq: RRule.WEEKLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]],
        })
          .toString()
          .split('RRULE:')[1],
      },
      {
        label: `Biweekly on ${selectedDay}`,
        value: new RRule({
          freq: RRule.WEEKLY,
          interval: 2,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]],
        })
          .toString()
          .split('RRULE:')[1],
      },
      {
        label: `Monthly on the ${numberToWords[nthDayOfTheMonth]} ${format(selectedDate, 'EEEE')}`,
        value: new RRule({
          freq: RRule.MONTHLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]?.nth(nthDayOfTheMonth)],
        })
          .toString()
          .split('RRULE:')[1],
      },
    ];

    // thanks to UAT feedback, code is messy now :(
    if (isLastNthDayOfTheMonth) {
      frequencies.push({
        label: `Monthly on the last ${format(selectedDate, 'EEEE')}`,
        value: new RRule({
          freq: RRule.MONTHLY,
          interval: 1,
          byweekday: [RRule[format(selectedDate, 'EEEEEE').toUpperCase()]?.nth(-1)],
        })
          .toString()
          .split('RRULE:')[1],
      });
    }
    const selectedFrequencyIsNotInFrequencies = !frequencies.some((frequency) => frequency.value === selectedFrequency);
    if (selectedFrequencyIsNotInFrequencies && selectedFrequency) {
      // capitalizing every word apart from "on" or "the" or "on the"
      const label = dayCapitalizedRRuleString(RRule.fromString(selectedFrequency).toText());
      frequencies.push({
        label,
        value: RRule.fromString(selectedFrequency).toString().split('RRULE:')[1],
        // id: 'selectedFrequency'
      });
    }
    frequencies.push({
      label: 'Custom',
      value: 'custom',
    });
    return frequencies;
  }, [selectedDate, selectedFrequency]);

  const selectedFrequencyOption =
    meetingFrequencies.find((frequency) => frequency.value === selectedFrequency) ??
    meetingFrequencies[meetingFrequencies.length - 1];

  console.log({ selectedFrequencyOption });

  const frequencyOnChange = ({ value }: { value: string }) => {
    if (value === 'custom') {
      setIsCustomRecurrenceModalOpen(true);
      return;
    }
    dispatch(updateFrequency(value));
  };

  const durationOnChange = ({ value }: { value: string }) => {
    dispatch(updateDuration(Number(value)));
  };
  // modal states
  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);

  const onCustomRecurrenceSaveHandler = (recurrenceRule: string) => {
    dispatch(updateFrequency(recurrenceRule));
    setIsCustomRecurrenceModalOpen(false);
  };

  useEffect(() => {
    if (!selectedFrequency) {
      dispatch(updateFrequency(meetingFrequencies?.[0]?.value));
    }
  }, [meetingFrequencies, selectedFrequency]);

  return (
    <>
      <Flex w="100%" flexDir="column">
        <Container>
          <Flex
            sx={{
              '.mesh-date-picker-button': {
                width: '100%',
                alignItems: 'center',
                justifyContent: 'flex-start',
                borderRadius: 0,
              },
              _hover: {
                '.mesh-date-picker-button': {
                  borderBottom: '1px solid #333333 !important',
                },
                '.mesh-date-picker-button-open': {
                  borderBottom: '1px solid #1565bc !important',
                },
              },
              '.mesh-date-picker-button-open': {
                borderBottom: '1px solid #1565bc !important',
              },
            }}
            flexDir="column"
            alignItems="flex-start"
            mb={3}
          >
            <TextLabel color="text.g2">Select Date</TextLabel>
            <DatePicker
              onChange={(date) => dispatch(updateDateTime(date))}
              selectedDate={selectedDate}
              popperPlacement="auto"
              minDate={new Date()}
            />
          </Flex>
          <Grid templateColumns="repeat(2, 1fr)">
            <GridItem>
              <Flex
                sx={{
                  '.mesh-time-picker-button': {
                    width: '90%',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    borderRadius: 0,
                  },
                  _hover: {
                    '.mesh-time-picker-button': {
                      borderBottom: '1px solid #333333 !important',
                    },
                    '.mesh-date-picker-button-open': {
                      borderBottom: '1px solid #1565bc !important',
                    },
                  },
                  '.mesh-date-picker-button-open': {
                    borderBottom: '1px solid #1565bc !important',
                  },
                }}
                flexDir="column"
                alignItems="flex-start"
              >
                <TextLabel color="text.g2">Select Time</TextLabel>
                <DatePicker
                  disabled={!selectedDate}
                  onChange={(date) => {
                    dispatch(updateDateTime(date));
                  }}
                  selectedDate={selectedDate}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  // timeCaption="Meeting Time"
                  dateFormat="h:mm aa"
                  CustomInput={TimeCustomInput}
                  // disable past time to be selected in Date type
                  minTime={selectedDate && isToday(selectedDate) ? new Date() : undefined}
                  maxTime={selectedDate && isToday(selectedDate) ? new Date(0, 0, 0, 23, 59) : undefined}
                />
                <TextLabel color="text.g3" mt="4px">
                  {employeeTimezone}
                </TextLabel>
              </Flex>
            </GridItem>
            <GridItem>
              <Flex flexDir="column" alignItems="flex-start">
                <TextLabel color="text.g2">Select Duration</TextLabel>
                {/* @ts-ignore */}
                <SelectDashed
                  handleChange={durationOnChange}
                  options={DURATION_OPTIONS}
                  isSearchable={false}
                  value={selectedDuration}
                />
              </Flex>
            </GridItem>
          </Grid>
          <Flex flexDir="column" alignItems="flex-start" mt="12px">
            <TextLabel color="text.g2">Select Frequency</TextLabel>
            {/* @ts-ignore */}
            <SelectDashed
              options={meetingFrequencies}
              isSearchable={false}
              value={selectedFrequencyOption}
              handleChange={frequencyOnChange}
            />
          </Flex>
        </Container>
      </Flex>
      {isCustomRecurrenceModalOpen && (
        <CustomRecurrenceModal
          isOpen={isCustomRecurrenceModalOpen}
          onClose={() => {
            setIsCustomRecurrenceModalOpen(false);
          }}
          onSave={onCustomRecurrenceSaveHandler}
          selectedDate={selectedDate}
          recurrenceRule={selectedFrequency ?? ''}
        />
      )}
    </>
  );
};

export { MeetingsDetails };
